import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/hooks/useProfile";

/**
 * Course ID for "Foundations: Building a Sustainable Business"
 * 
 * CANONICAL IDENTIFIER - If this course ID ever changes, update it here.
 * This matches the constant in the backend SQL function `has_completed_foundations_course`.
 */
export const FOUNDATIONS_COURSE_ID = "7fd264f5-32f3-459c-8401-0446296dcf8b";
export const FOUNDATIONS_COURSE_SLUG = "foundations-building-a-sustainable-business";
export const FOUNDATIONS_COURSE_TITLE = "Foundations: Building a Sustainable Business";

interface FoundationsEligibility {
  /** Whether the user can request mentorship */
  canRequestMentorship: boolean;
  /** Whether the user has completed the Foundations course */
  hasCompletedCourse: boolean;
  /** Whether the user is enrolled in the course */
  isEnrolled: boolean;
  /** Progress percentage (0-100) */
  progress: number;
  /** Whether the check is still loading */
  isLoading: boolean;
  /** Whether the user is a mentor (always eligible) */
  isMentor: boolean;
  /** Course link for CTA */
  courseLink: string;
}

/**
 * Hook to check if the current user is eligible to request mentorship.
 * 
 * Entrepreneurs must complete "Foundations: Building a Sustainable Business"
 * before they can request mentorship from any mentor.
 * 
 * Mentors and admins are always eligible (they don't create entrepreneur requests).
 */
export function useFoundationsEligibility(): FoundationsEligibility {
  const { user } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile();

  const { data: eligibilityData, isLoading: eligibilityLoading } = useQuery({
    queryKey: ["foundations-eligibility", user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return {
          canRequestMentorship: false,
          hasCompletedCourse: false,
          isEnrolled: false,
          progress: 0,
        };
      }

      // Call the RPC function to check eligibility
      const { data: canRequest, error: rpcError } = await supabase.rpc(
        "can_request_mentorship",
        { p_user_id: user.id }
      );

      if (rpcError) {
        console.error("Error checking mentorship eligibility:", rpcError);
      }

      // Also fetch enrollment and progress for UI display
      const { data: enrollment } = await supabase
        .from("course_enrollments")
        .select("id, completed_at")
        .eq("user_id", user.id)
        .eq("course_id", FOUNDATIONS_COURSE_ID)
        .maybeSingle();

      let progress = 0;
      if (enrollment) {
        // Use the existing get_course_progress function
        const { data: progressData } = await supabase.rpc("get_course_progress", {
          _user_id: user.id,
          _course_id: FOUNDATIONS_COURSE_ID,
        });
        progress = progressData || 0;
      }

      return {
        canRequestMentorship: canRequest ?? false,
        hasCompletedCourse: progress === 100,
        isEnrolled: !!enrollment,
        progress,
      };
    },
    enabled: !!user?.id && !!profile,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const isMentor = profile?.role === "mentor";
  const isLoading = profileLoading || eligibilityLoading;

  return {
    canRequestMentorship: isMentor || (eligibilityData?.canRequestMentorship ?? false),
    hasCompletedCourse: eligibilityData?.hasCompletedCourse ?? false,
    isEnrolled: eligibilityData?.isEnrolled ?? false,
    progress: eligibilityData?.progress ?? 0,
    isLoading,
    isMentor,
    courseLink: `/courses/${FOUNDATIONS_COURSE_ID}`,
  };
}
